import { Component, Input, OnInit } from '@angular/core';
import { BlockUI, NgBlockUI }       from 'ng-block-ui';
import { NgForm }                   from '@angular/forms';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { AzureSsoService }             from '../azure-sso/azure-sso.service';
import { BusinessIntelligenceService } from '../business-intelligence/business-intelligence.service';
import { CodeTypeModel }               from '../core/models/code-type';
import { CompanyService }              from '../companies/company.service';
import { ConfirmService }              from '../messages/confirm/confirm.service';
import { GroupModel }                  from '../core/models/group';
import { MessagesService }             from '../messages/messages.service';
import { SessionService }              from '../sessions/session.service';
import { UserModel }                   from './typings/user';
import { UserNotificationTypeModel }   from './typings/user.notification-type';
import { UsersMechanicModel }          from './typings/users.mechanic';
import { UsersService }                from './users.service';

@Component({
  selector   : 'wor-user',
  template: require('./user.component.html')
})
export class UserComponent implements OnInit {
  @BlockUI('userBlock') block : NgBlockUI;

  @Input() notificationTypes : Array<UserNotificationTypeModel> = [];
  @Input() session  : any;
  @Input() user     : UserModel;
  @Input() userQuote: any;

  groups     : Array<GroupModel> = [];
  statusTypes: Array<CodeTypeModel> = [];

  originalBiStatus       = false;
  originalMobileMechanic = false;
  selectedMechanic       : UsersMechanicModel = {};

  constructor (
    private azureSsoService            : AzureSsoService,
    public  businessIntelligenceService: BusinessIntelligenceService,
    public  companyService             : CompanyService,
    private confirmService             : ConfirmService,
    private messagesService            : MessagesService,
    private router                     : UIRouter,
    public  sessionService             : SessionService,
    private translateService           : TranslateService,
    private usersService               : UsersService
  ) {}

  ngOnInit () : void {
    this.user = this.user.id
      ? this.user
      : {
          dashboard_privileges: 'L',
          state               : 'ACTIVE'
        };

    this.user.confirm_password  = this.user.password;
    this.originalBiStatus       = !!this.user.business_intelligence_user;
    this.originalMobileMechanic = !!this.user.mobile_mechanic;

    this.selectedMechanic = this.user.mechanic_id
      ? {
        display_name: this.user.mechanic_display_name,
        id          : this.user.mechanic_id
      }
      : {};

    this.usersService.getGroups()
    .then(groups => this.groups = groups);

    this.usersService.getStateTypes()
    .then(types => this.statusTypes = types);
  }

  canResetTwoFactorAuth () : boolean {
    return !!this.user.id
      && this.companyService.isTwoFactorAuthEnabled();
  }

  chooseMechanic ( mechanic : UsersMechanicModel ) : void {
    if ( !mechanic || !mechanic.id ) {
      return;
    }

    this.user.first_name            = mechanic.first_name;
    this.user.last_name             = mechanic.last_name;
    this.user.mechanic_display_name = mechanic.display_name;
    this.user.mechanic_id           = mechanic.id;
  }

  delete ( form : NgForm ) : void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_USER'))
    .then(() => this.usersService.deleteUser(this.user))
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.USER'), 'success');

      this.router.stateService.go('app.users');
    })
    .catch(error => this.messagesService.error(error));
  }

  formatMechanic ( mechanic : UsersMechanicModel ) : string {
    return mechanic?.display_name || '';
  }

  /**
   * The backend applies no mobile defaults, so the create form pre-sets the admin ones here,
   * touching only toggles the screen shows and never an existing user's stored settings
   */
  handleGroupChange ( group : string ) : void {
    if ( this.user.id ) {
      return;
    }

    const isAdmin = group === 'ADMIN';

    if (this.companyService.isSubscriptionPlan('gold', false) && this.companyService.hasWorkshop()) {
      this.user.advanced_mobile_user          = isAdmin;
      this.user.advanced_mobile_user_invoices = isAdmin;
      this.user.advanced_mobile_user_pricing  = isAdmin;
    }

    if (this.companyService.hasDealership() && this.companyService.hasWorkshop()) {
      this.user.mobile_dealer = isAdmin;
    }
  }

  hasQuote () : boolean {
    return !this.user.id
      && this.userQuote
      && !this.sessionService.base().capricorn_user;
  }

  isAdvancedUser () : boolean {
    return !!this.user.advanced_mobile_user;
  }

  isMobileMechanic () : boolean {
    return !!this.user.mobile_mechanic;
  };

  reconnectToAzure () : void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.RECONNECT_TO_AZURE'))
    .then(() => this.azureSsoService.reconnectUser(this.user))
    .catch(error => this.messagesService.error(error));
  }

  resetTwoFactorAuth ( form : NgForm ) : void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.RESET_TFA'))
    .then(() => this.usersService.resetTwoFactorAuth(this.user))
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.TFA_RESET'), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  save ( form : NgForm ) : void {
    this.block.start();

    if (!this.user.mobile_mechanic) {
      this.user.mobile_limit_event_times = false;

      /**
       * Unlink instead of omitting the key. Leaving mechanic_id out of the payload keeps the
       * stored mechanic, so the user stays linked to it and that mechanic cannot be given to
       * anyone else
       */
      if (this.originalMobileMechanic) {
        this.user.mechanic_id = undefined;
      }
    }

    /**
     * A hidden child toggle must not keep saving the value it last held. Parts of the backend read
     * the advanced flags on their own, so a child left on under an off parent grants mobile access
     * the screen no longer shows
     */
    if (!this.user.advanced_mobile_user) {
      this.user.advanced_mobile_user_invoices = false;
      this.user.advanced_mobile_user_pricing  = false;
    }

    Promise.all(this.notificationTypes.map(notification => this.usersService.updateMobileNotificationType(notification, this.user.id)))
    .then(() => this.user.id
      ? this.usersService.updateUser(this.user)
      : this.usersService.createUser(this.user))
    .then(( user : UserModel ) => {
      this.user.id = user.id;

      return this.originalBiStatus !== !!this.user.business_intelligence_user
        ? this.businessIntelligenceService.updateStatus(this.user)
        : Promise.resolve(this.user);
    })
    .then(( user : UserModel ) => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.USER'), 'success');

      this.router.stateService.go('app.user', {
        id: user.id
      }, {
        reload: true
      });
    })
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  searchMechanics = ( typed : string ) : Promise<Array<UsersMechanicModel>> => this.usersService.searchMechanics(typed);
}
